let loadPromise: Promise<typeof Spotify> | null = null;

const SCRIPT_ID = 'spotify-web-playback-sdk';
const SDK_URL = 'https://sdk.scdn.co/spotify-player.js';
const LOAD_TIMEOUT_MS = 10000;

const isDevelopment = typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

function resolveWithSpotify(resolve: (value: typeof Spotify) => void): boolean {
  const spotifyNamespace = (window as any).Spotify;
  if (spotifyNamespace) {
    resolve(spotifyNamespace as typeof Spotify);
    return true;
  }
  return false;
}

function cleanup(script: HTMLScriptElement | null, onLoad: () => void, onError: () => void, timeoutId?: number) {
  if (typeof timeoutId === 'number') {
    window.clearTimeout(timeoutId);
  }
  if (script) {
    script.removeEventListener('load', onLoad);
    script.removeEventListener('error', onError);
  }
  delete (window as any).onSpotifyWebPlaybackSDKReady;
}

export const loadSpotifyWebPlaybackSdk = (): Promise<typeof Spotify> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Spotify Web Playback SDK cannot load on the server.'));
  }

  if ((window as any).Spotify) {
    console.info('[DeviceInitTiming] SDK already loaded', {
      timestamp: new Date().toISOString(),
      durationMs: 0,
    });
    return Promise.resolve((window as any).Spotify as typeof Spotify);
  }

  if (loadPromise) {
    console.info('[DeviceInitTiming] SDK load already in progress, waiting', {
      timestamp: new Date().toISOString(),
    });
    return loadPromise;
  }

  const sdkLoadStartTime = Date.now();
  console.info('[DeviceInitTiming] SDK load start', {
    timestamp: new Date().toISOString(),
  });

  loadPromise = new Promise<typeof Spotify>((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    let script: HTMLScriptElement | null = existingScript;
    let timeoutId: number | undefined;

    const handleSuccess = () => {
      if (resolveWithSpotify(resolve)) {
        const duration = Date.now() - sdkLoadStartTime;
        console.info('[DeviceInitTiming] SDK load complete', {
          timestamp: new Date().toISOString(),
          durationMs: duration,
        });
        cleanup(script, handleSuccess, handleFailure, timeoutId);
      }
    };

    const handleFailure = (error?: ErrorEvent | Error) => {
      const duration = Date.now() - sdkLoadStartTime;
      console.error('[DeviceInitTiming] SDK load failed', {
        timestamp: new Date().toISOString(),
        durationMs: duration,
        error: error instanceof Error ? error.message : String(error),
      });
      cleanup(script, handleSuccess, handleFailure, timeoutId);
      loadPromise = null;
      reject(error instanceof Error ? error : new Error('Failed to load Spotify Web Playback SDK.'));
    };

    (window as any).onSpotifyWebPlaybackSDKReady = () => {
      if (resolveWithSpotify(resolve)) {
        const duration = Date.now() - sdkLoadStartTime;
        console.info('[DeviceInitTiming] SDK load complete (onSpotifyWebPlaybackSDKReady)', {
          timestamp: new Date().toISOString(),
          durationMs: duration,
        });
        cleanup(script, handleSuccess, handleFailure, timeoutId);
      }
    };

    timeoutId = window.setTimeout(() => {
      if (!resolveWithSpotify(resolve)) {
        handleFailure(new Error('Spotify Web Playback SDK did not initialize in time.'));
      }
    }, LOAD_TIMEOUT_MS);

    if (script) {
      script.addEventListener('load', handleSuccess, { once: true });
      script.addEventListener('error', () => handleFailure(), { once: true });
      if (resolveWithSpotify(resolve)) {
        cleanup(script, handleSuccess, handleFailure, timeoutId);
      }
      return;
    }

    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleSuccess, { once: true });
    script.addEventListener('error', () => handleFailure(), { once: true });
    document.body.appendChild(script);
  });

  if (isDevelopment) {
    loadPromise.catch((error) => {
      console.error('[Spotify SDK] failed to load', error);
    });
  }

  return loadPromise;
};

export const resetSpotifyWebPlaybackLoader = () => {
  loadPromise = null;
  delete (window as any).Spotify;
  delete (window as any).onSpotifyWebPlaybackSDKReady;
  const script = document.getElementById(SCRIPT_ID);
  if (script && script.parentElement) {
    script.parentElement.removeChild(script);
  }
};
